/**
 * Brand registry for host-based brand resolution
 * Maps runtime host → brand key for single-build, multi-brand SPA
 */

export const DEFAULT_BRAND = '100xdevelopers.com';

/**
 * Host to brand mapping for production and development
 */
export const HOST_TO_BRAND: Record<string, string> = {
    // Production hosts
    '100xdevelopers.com': '100xdevelopers.com',
    'experimentofone.com': 'experimentofone.com',

    // Development hosts (.local)
    '100xdevelopers.local': '100xdevelopers.com',
    'experimentofone.local': 'experimentofone.com'
};

/**
 * Normalizes a host string by removing port, PR prefixes, and www prefix
 * @param host - The host string (e.g., "www.pr-123.100xdevelopers.com:5173")
 * @returns Normalized host (e.g., "100xdevelopers.com")
 */
export function normalizeHost(host: string): string {
    if (!host) return DEFAULT_BRAND;

    // Remove port (everything after the last colon)
    let normalized = host.split(':')[0];

    // Remove www. prefix first
    normalized = normalized.replace(/^www\./, '');

    // Remove PR prefix (pr-<N>.)
    normalized = normalized.replace(/^pr-\d+\./, '');

    return normalized;
}

/**
 * Resolves a host to its corresponding brand key
 * @param host - The host string to resolve
 * @returns The brand key, or DEFAULT_BRAND if not found
 */
export function hostToBrand(host: string): string {
    const normalized = normalizeHost(host);
    return HOST_TO_BRAND[normalized] || DEFAULT_BRAND;
}

/**
 * Unique brand keys used for deploy/prerender (every value in {@link HOST_TO_BRAND}).
 * Authoritative list: static/brands/&lt;key&gt; must exist for each.
 */
export function getDeployBrandKeys(): string[] {
    return [...new Set(Object.values(HOST_TO_BRAND))].sort();
}

/**
 * Preferred production hostname (apex .com) for a brand, for canonical URLs and prerender origin.
 */
export function getCanonicalHostnameForBrand(brandKey: string): string | undefined {
    const hosts = Object.entries(HOST_TO_BRAND)
        .filter(([, v]) => v === brandKey)
        .map(([h]) => h)
        .filter(h => h.endsWith('.com') && !h.startsWith('www.'));
    hosts.sort();
    return hosts[0];
}

/**
 * HTTPS origin for prerendering a brand (matches {@link getCanonicalHostnameForBrand}).
 */
export function getPrerenderOriginForBrand(brandKey: string): string | undefined {
    const host = getCanonicalHostnameForBrand(brandKey);
    return host ? `https://${host}` : undefined;
}

/**
 * PR preview origin for a brand (matches Cypress / CloudFront preview hosts).
 */
export function getPrerenderOriginForBrandAndPr(brandKey: string, prNumber: string): string {
    const host = getCanonicalHostnameForBrand(brandKey);
    if (!host) {
        throw new Error(`No canonical hostname for brand ${brandKey}`);
    }
    return `https://pr-${prNumber}.${host}`;
}
